import { FC } from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import queryString from 'query-string';
import { ButtonPrimary, Form, InputText } from '@lambdacurry/component-library';
import { useMeQuery, useUpdateUserMutation, useCheckPostalCodeMutation } from 'lib/home/generated/queries';
import { addressValidation, nameValidation } from 'lib/forms/validation';
import { Card, CardContent, Grid } from 'lib/components';
import { RegistrationRouteMap, RegistrationStep, useRegistrationStore } from '../../store';

import {
  GettingStartedContent,
  GettingStartedLayout,
  GettingStartedHealthyQuickEasyFooter,
  InputGrid,
  NameInputGroup
} from '../../components';

interface GettingStartedFormValues {
  first_name: string;
  last_name: string;
  postal_code: string;
}

const validationSchema = Yup.object({
  ...nameValidation,
  postal_code: addressValidation.postal_code
});

const GettingStartedPage: FC = () => {
  const { checkoutInfo, setCheckoutInfo, setPromoCode, setReferralCode } = useRegistrationStore();
  const { data: user, isError } = useMeQuery();
  const { mutate: updateUser } = useUpdateUserMutation();
  const { mutate: checkPostalCode } = useCheckPostalCodeMutation();
  const isAuthorized = !isError && !!user;

  const location = useLocation();
  const queries = queryString.parse(location.search);
  const { promo, referralCode } = queries;

  const initialValues: GettingStartedFormValues = {
    first_name: checkoutInfo.first_name || '',
    last_name: checkoutInfo.last_name || '',
    postal_code: checkoutInfo.postal_code || ''
  };

  const saveAndContinue = (values: GettingStartedFormValues) => {
    if (isAuthorized) updateUser({ userInput: { ...values, id: user.me.id } });

    navigate(RegistrationRouteMap[RegistrationStep.selectPlan]);
  };

  const onSubmit = (values: GettingStartedFormValues, formikHelpers: FormikHelpers<GettingStartedFormValues>) => {
    const { postal_code } = values;
    if (promo) setPromoCode(promo as string);
    else setPromoCode(undefined);
    if (referralCode) setReferralCode(referralCode as string);
    formikHelpers.setSubmitting(true);
    setCheckoutInfo(values);
    checkPostalCode(
      { code: values.postal_code },
      {
        onSuccess: result => {
          const { postalCodeAllowed } = result;
          if (!postalCodeAllowed)
            return navigate(RegistrationRouteMap.unsupportedPostalCode, { state: { postal_code } });
          saveAndContinue(values);
        }
      }
    );
  };

  return (
    <GettingStartedLayout className="getting-started-index" step={RegistrationStep.index}>
      <GettingStartedContent>
        <Grid>
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Card>
              <div className="flex flex-col-reverse lg:flex-row lg:flex">
                <CardContent>
                  <h2>
                    Personal Chef Meals
                    <br />
                    Delivered To You
                  </h2>
                  <Form
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    validationSchema={validationSchema}
                  >
                    {formikProps => (
                      <>
                        <InputGrid>
                          <NameInputGroup />
                          <InputText label="ZIP Code" type="text" name="postal_code" />
                        </InputGrid>

                        <div className="mt-8 flex flex-col md:flex-row justify-start gap-4 md:gap-8">
                          <ButtonPrimary
                            data-cy="submit-button"
                            type="submit"
                            className="md:w-56"
                            disabled={formikProps.isSubmitting || !formikProps.isValid}
                          >
                            Continue
                          </ButtonPrimary>
                        </div>
                      </>
                    )}
                  </Form>
                </CardContent>
                <div className="getting-started-index-graphic w-full h-44"></div>
              </div>
            </Card>
          </div>
        </Grid>
      </GettingStartedContent>

      <GettingStartedHealthyQuickEasyFooter />
    </GettingStartedLayout>
  );
};

export default GettingStartedPage;
